// The console's Accounts screen and Developer switch (servers-and-developers WEB-SRV-DEV-01 to -04, VisionPlatform
// app-server-selection SRV-11, SRV-11a, SRV-14) over vision-api 0.25.0's findCustomerUser and setUserDeveloper.
import type { components } from "../../../api/generated/vision-api";
import { apiClient, unwrap } from "../../../api/client";
import { ApiError } from "../../../api/problem";

export type CustomerUser = components["schemas"]["CustomerUser"];
/** A staff grant; `cases` is absent from servers older than vision-api 0.28.0. */
type GrantLike = Omit<components["schemas"]["StaffGrant"], "cases"> & { cases?: boolean };

/**
 * Whether the person may look accounts up (WEB-SRV-DEV-03): `user.developer`, and `identity.read` for every customer,
 * not one limited to some organisations, products or rings. The API decides; this only hides what it would refuse.
 */
export function canManageDevelopers(grants: readonly GrantLike[] | undefined): boolean {
  // identity.read limited to the cases a person works (vision-api 0.28.0 `cases`) is not "every customer" either.
  const all = (g: GrantLike) => !g.org_ids?.length && !g.products?.length && !g.rings?.length && !g.cases;
  const list = grants ?? [];
  return (
    list.some((g) => g.capability === "user.developer") && list.some((g) => g.capability === "identity.read" && all(g))
  );
}

/** A refused lookup or change in words: no account, a missing capability, the hourly limit, not built yet. */
export function accountError(e: unknown, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!(e instanceof ApiError)) return t("accounts.error.unknown");
  if (e.status === 404) return t("accounts.error.notFound");
  if (e.status === 403) return t("accounts.error.forbidden");
  if (e.status === 429) {
    return e.retryAfterMinutes
      ? t("accounts.error.limitMinutes", { count: e.retryAfterMinutes })
      : t("accounts.error.limit");
  }
  if (e.status === 501) return t("accounts.error.notYet");
  return e.message;
}

export function findAccount(email: string): Promise<CustomerUser> {
  return unwrap(apiClient("ops").GET("/v1/ops/users", { params: { query: { email } } }));
}

export async function setDeveloper(userId: string, developer: boolean): Promise<void> {
  await unwrap(
    apiClient("ops").PUT("/v1/ops/users/{user_id}/developer", {
      params: { path: { user_id: userId } },
      body: { developer },
    }),
  );
}
